"use client"

import { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  alpha,
  Tooltip,
  Modal,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Autocomplete,
  Alert,
  CircularProgress,
  Collapse,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import BallotIcon from "@mui/icons-material/Ballot"
import SaveIcon from "@mui/icons-material/Save"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import FilterListIcon from "@mui/icons-material/FilterList"
import ClearIcon from "@mui/icons-material/Clear"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import PercentIcon from "@mui/icons-material/Percent"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import GroupsIcon from "@mui/icons-material/Groups"

import dayjs from "dayjs"
import Swal from "sweetalert2"
import jsPDF from "jspdf"
import "jspdf-autotable"
import CustomPagination from "../transaccionComponent/CustomPagination.js"
import FormularioVerBalance from "./FormularioVerBalance.js"
import { generarPdfVerBalance } from "../../context/pdf_esqueletos/verBalancePdf"
import { deliverPdf, reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"
import { getPasajeroNombrePdf, sortPasajerosPdf } from "../../context/pdf_esqueletos/helpers/pasajerosPdf.js"
import { getVendedoresTexto } from "../../context/pdf_esqueletos/helpers/vendedoresPdf.js"

// Tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
  },
})

const TablaVerBalance = () => {
  const {
    isNavbarCollapsed,
    getItemTresLetras,
    arrayItemTresLetras,
    itemTresLetras,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    crearRecetaApi,
    eliminarBomApi,
    getRecetasViajes,
    getBomByIdItem,
    getEntidadFilterTipoEntidad,
    getUbicacion,
    ubicaciones,
    getItemUbicacionFilterByUbicacion,
    getFechaHoraActualClient,
    getParametrosGlobales,
    getTablaRecetaDatos,
    getTablaRecetaTotales,
    getTablaRecetaFinancieros,
    getClientesPorGrupoYDestino,
    setCerradoPaqueteApi,
    setCanceladoPaqueteApi,
    user,
    tieneAccesoFuncionalidad,
  } = useContext(ErpContext)

  // Permisos granulares (INVENTARIO8 - Ver Balance)
  const mostrarPrecioOriginalFooter = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_verPrecioOriginalFooter")
  const mostrarPrecioTotalFooter = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_verPrecioTotalFooter")
  const mostrarCostoTotalFooter = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_verCostoTotalFooter")
  const mostrarGananciaFooter = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_verGananciaFooter")
  const mostrarColumnaPrecioOriginal = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_columnaPrecioOriginal")
  const mostrarColumnaPrecioTotal = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_columnaPrecioTotal")
  const mostrarColumnaCostoTotal = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_columnaCostoTotal")
  const mostrarColumnaGanancia = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_columnaGanancia")
  const mostrarBtnEditarGrupo = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_btnEditarGrupo")
  const mostrarBtnDescargarPdfPasajeros = tieneAccesoFuncionalidad(user, "INVENTARIO8", "INVENTARIO8_btnDescargarPdfPasajeros")
  const [recetas, setRecetas] = useState([])
  const [filteredRecetas, setFilteredRecetas] = useState([])
  const [loading, setLoading] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedReceta, setSelectedReceta] = useState(null)
  const [editedIngredientes, setEditedIngredientes] = useState([])
  const [preciosCostos, setPreciosCostos] = useState({})
  const [precioPaquete, setPrecioPaquete] = useState(0)
  const [cargandoDatosModal, setCargandoDatosModal] = useState(false)

  const today = dayjs()
  // 🔧 ELIMINADO: Filtros de fecha inicio/fin - ya no se usan para filtrar
  // Las fechas inicio/fin se siguen mostrando en la tabla pero no filtran
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const DEFAULT_SORT = [{ field: "fechaInicio", sort: "desc" }]
  const [sortModel, setSortModel] = useState(DEFAULT_SORT)
  const SORT_FIELD_LABELS = {
    fechaInicio: "Fecha Inicio",
    codigoProductoTerminado: "Grupo",
    destino: "Destino",
    precioOriginal: "Precio Original",
    precioPorPasajeros: "Precio Total",
    costoTotalServicios: "Costo Total",
    ganancia: "Ganancia",
    porcentajeGanancia: "% Ganancia",
  }
  // Estados para totales de todas las páginas
  const [totalPrecioOriginal, setTotalPrecioOriginal] = useState(0)
  const [totalPrecioTotal, setTotalPrecioTotal] = useState(0)
  const [totalCostoTotal, setTotalCostoTotal] = useState(0)
  const [totalGanancia, setTotalGanancia] = useState(0)
  const [loadingTotales, setLoadingTotales] = useState(false)
  const totalesReqIdRef = useRef(0)

  // Columnas ocultas por defecto (toggleables con 3 puntos).
  // Las de permiso solo se fuerzan ocultas cuando el usuario no tiene acceso.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
    fechaCreacion: false,
    fechaFin: false,
    cerrado: false,
    noches: false,
    totalIngredientes: false,
    pasajeros: false,
    fechaActualizacion: false,
  })

  const fetchLogoTenant = useCallback(async () => {
    try {
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      return params?.logoNavbarTenant || null
    } catch (error) {
      console.error("Error cargando logo del tenant:", error)
      return null
    }
  }, [getParametrosGlobales])

  const fetchPdfConfig = useCallback(async () => {
    try {
      const params = await getParametrosGlobales([
        "logoNavbarTenant",
        "verFechaSalidaYFechaFinPdf",
        "verFechaSalidaPdf",
      ])
      const fechaViajePdfConfig =
        params?.verFechaSalidaYFechaFinPdf ?? params?.verFechaSalidaPdf
      return {
        logoTenant: params?.logoNavbarTenant || null,
        showFechaSalida:
          fechaViajePdfConfig === 1 ||
          fechaViajePdfConfig === "1" ||
          fechaViajePdfConfig === true ||
          fechaViajePdfConfig === "true",
      }
    } catch (error) {
      console.error("Error cargando configuración de PDFs:", error)
      return {
        logoTenant: null,
        showFechaSalida: true,
      }
    }
  }, [getParametrosGlobales])

  // Estados para filtros
  const [searchTextReceta, setSearchTextReceta] = useState("")
  const [filtroProducto, setFiltroProducto] = useState("")
  const [filtroDestino, setFiltroDestino] = useState("")

  const [recetaSelected, setRecetaSelected] = useState(null)
  const [destinoSelected, setDestinoSelected] = useState(null)
  
  // 🔧 NUEVO: Filtros por fecha de viaje (fechaInicio del paquete)
  // Inicializar con el año actual: 1/1 a 31/12
  const [fechaViajeDesde, setFechaViajeDesde] = useState(today.startOf("year"))
  const [fechaViajeHasta, setFechaViajeHasta] = useState(today.endOf("year"))
  // 🔧 NUEVO: Filtro por fecha de inicio exacta
  const [fechaInicioSelected, setFechaInicioSelected] = useState(null)

  // 🔧 NUEVO: Estados para filtros de estado (cerrado/abierto/cancelado)

  const [filtroCerrado, setFiltroCerrado] = useState(false)
  const [filtroAbierto, setFiltroAbierto] = useState(false)
  const [filtroCancelado, setFiltroCancelado] = useState(false)

  // Estado para tracking de cambios pendientes en estado de paquetes
  const [cambiosPendientes, setCambiosPendientes] = useState(new Map())
  // Map<idPaquete, { estadoOriginal: boolean, estadoNuevo: boolean, ...datosDelPaquete }>

  // Estado para modal de servicios
  const [modalServicios, setModalServicios] = useState({
    open: false,
    receta: null,
    servicios: []
  })
    // Estado para modal de pasajeros confirmados (lazy load)
    const [modalPasajerosConfirmados, setModalPasajerosConfirmados] = useState({
      open: false,
      receta: null,
      pasajeros: [],
      loading: false,
      error: null,
    })
  const [paquetesOcultarCantidadFlag, setPaquetesOcultarCantidadFlag] = useState(null)
  const [modalEdicionRequiresRefetch, setModalEdicionRequiresRefetch] = useState(false)

  // Estado para modal de edición
  const [modalEdicion, setModalEdicion] = useState({
    open: false,
    paquete: null,
    ocultarCantidadYCostoXCantidad: null,
  })

  // Estado para modal de crear paquete
  const [openCrearPaqueteModal, setOpenCrearPaqueteModal] = useState(false)
  const [grupoSeleccionadoCrear, setGrupoSeleccionadoCrear] = useState(null) // ✅ Para mostrar el grupo seleccionado en el header

  // Estado para controlar visibilidad de filtros
  const isLaptopDown = useMediaQuery(theme.breakpoints.down("lg"))
  const [filtersOpen, setFiltersOpen] = useState(!isLaptopDown)
  
  // Ajustar filtersOpen cuando cambia el tamaño de pantalla
  useEffect(() => {
    setFiltersOpen(!isLaptopDown)
  }, [isLaptopDown])

  const ensurePaquetesOcultarCantidadFlag = useCallback(async () => {
    if (paquetesOcultarCantidadFlag !== null) {
      return paquetesOcultarCantidadFlag
    }

    if (typeof getParametrosGlobales !== "function") {
      setPaquetesOcultarCantidadFlag(false)
      return false
    }

    try {
      const res = await getParametrosGlobales(["paquetesOcultarCantidadYCostoXCantidad"])
      const raw = res?.paquetesOcultarCantidadYCostoXCantidad
      const parsed =
        raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "TRUE"

      setPaquetesOcultarCantidadFlag(Boolean(parsed))
      return Boolean(parsed)
    } catch (error) {
      console.error("Error obteniendo parámetro paquetesOcultarCantidadYCostoXCantidad:", error)
      setPaquetesOcultarCantidadFlag(false)
      return false
    }
  }, [paquetesOcultarCantidadFlag, getParametrosGlobales])

  // ✅ OPTIMIZADO: useEffect de ensurePaquetesOcultarCantidadFlag eliminado
  // El parámetro ahora se carga en paralelo con getUbicacion y getItemAtributoNoEliminados
  // en el useEffect de inicialización (ver más abajo)

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchTextReceta.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(searchTextReceta, true)
    }
  }, [searchTextReceta])

  // 🔧 ELIMINADO: Efecto para actualizar endDate (ya no se usa)

  // ✅ OPTIMIZADO: Función para generar etiqueta de ítem con useCallback
  const generateRecetaLabel = useCallback((item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} ${attributeValues} ${item.codigoScanner}`
  }, [itemAtributoNoEliminados])

  // Función para manejar cambio de ítem
  const handleRecetaChange = (event, newValue) => {
    setRecetaSelected(newValue)
    if (newValue) {
      const defaultValues = itemAtributoNoEliminados.map((attr, index) => ({
        value: newValue[`itemDatoAtributo${index + 1}`] || "",
      }))
    }
  }

  const handleDestinoChange = (event, newValue) => {
    setDestinoSelected(newValue)
  }

  // Funciones para modal de servicios (BOM bajo demanda si la fila no trae ingredientes)
  const handleOpenModalServicios = async (receta) => {
    let servicios = receta.ingredientes || []
    if (servicios.length === 0 && receta.idProductoTerminado) {
      try {
        const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
        servicios = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
      } catch (err) {
        console.error("Error cargando servicios del paquete:", err)
      }
    }
    setModalServicios({
      open: true,
      receta: { ...receta, ingredientes: servicios },
      servicios,
    })
  }

  const handleCloseModalServicios = () => {
    setModalServicios({
      open: false,
      receta: null,
      servicios: []
    })
  }

  const handleOpenModalPasajerosConfirmados = async (receta) => {
    setModalPasajerosConfirmados({
      open: true,
      receta,
      pasajeros: [],
      loading: true,
      error: null,
    })

    try {
      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )

      const pasajerosConfirmados = (pasajerosData || []).filter((p) => p.viaja !== false)

      setModalPasajerosConfirmados((prev) => ({
        ...prev,
        pasajeros: pasajerosConfirmados,
        loading: false,
      }))
    } catch (error) {
      console.error("Error cargando pasajeros confirmados:", error)
      setModalPasajerosConfirmados((prev) => ({
        ...prev,
        loading: false,
        error: "No se pudieron cargar los pasajeros.",
      }))
    }
  }

  const handleCloseModalPasajerosConfirmados = () => {
    setModalPasajerosConfirmados({
      open: false,
      receta: null,
      pasajeros: [],
      loading: false,
      error: null,
    })
  }

  // Funciones para modal de edición
  const handleOpenModalEdicion = async (receta) => {
    const ocultarFlagPromise = ensurePaquetesOcultarCantidadFlag()
    // Cargar el item completo si no está en itemTresLetras
    const itemExistente = itemTresLetras?.find(it => it.id === receta.idProductoTerminado)
    if (!itemExistente && receta.codigoProductoTerminado) {
      // Buscar el item por código para obtener los atributos
      await getItemTresLetras(receta.codigoProductoTerminado.substring(0, 3))
    }
    
    // Asegurar que los atributos estén cargados
    if (!itemAtributoNoEliminados || itemAtributoNoEliminados.length === 0) {
      await getItemAtributoNoEliminados()
    }
    
    const ocultarFlag = await ocultarFlagPromise

    setModalEdicion({
      open: true,
      paquete: receta,
      ocultarCantidadYCostoXCantidad: ocultarFlag,
    })
    setModalEdicionRequiresRefetch(false)
  }

  const handleCloseModalEdicion = () => {
    setModalEdicion({
      open: false,
      paquete: null,
      ocultarCantidadYCostoXCantidad: null,
    })
    setModalEdicionRequiresRefetch(false)

    refetchTablaReceta()
  }

  const handleRecetaGuardada = () => {
    setModalEdicionRequiresRefetch(true)
    handleCloseModalEdicion()
  }

  const handleOpenCrearPaqueteModal = async () => {
    await ensurePaquetesOcultarCantidadFlag()
    setOpenCrearPaqueteModal(true)
  }

  const handleCloseCrearPaqueteModal = () => {
    setOpenCrearPaqueteModal(false)
    setGrupoSeleccionadoCrear(null) // ✅ Resetear el grupo seleccionado al cerrar
  }

  // Función para eliminar paquete
  const handleEliminarPaquete = async (receta) => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: `¿Deseas eliminar el paquete "${receta.descripcionProductoTerminado}" del destino "${receta.destino}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        setLoading(true)
        // Usar idPaquete si está disponible (más preciso), sino usar idProductoTerminado + destinoId
        await eliminarBomApi(receta.idProductoTerminado, receta.destinoId, receta.idPaquete || null)
        
        await Swal.fire({
          title: '¡Eliminado!',
          text: 'El paquete ha sido eliminado correctamente.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        })

        // Recargar datos de la tabla
        const idReceta = recetaSelected ? recetaSelected.id : null
        const idDestino = destinoSelected ? destinoSelected.id : null
        let cerradoParam = null
        let canceladoParam = null
        if (filtroCerrado) cerradoParam = true
        else if (filtroAbierto) cerradoParam = false
        else if (filtroCancelado) canceladoParam = true
        fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
      }
    } catch (error) {
      console.error("Error al eliminar paquete:", error)
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Error al eliminar el paquete',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePaqueteCreado = () => {
    // Cerrar el modal
    setOpenCrearPaqueteModal(false)
    // Refrescar la tabla después de crear el paquete
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    let canceladoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    else if (filtroCancelado) canceladoParam = true
    fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
  }

  // 🔧 FUNCIÓN PARA FORMATEAR MONEDA ARGENTINA
  const formatearMoneda = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '$0'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(valor)
  }

  // ✅ OPTIMIZADO: Generar atributos como subtexto con useCallback
  const generarAtributosSubtexto = useCallback((item) => {
    if (!item || !itemAtributoNoEliminados) return ""
    
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .filter((value) => value !== "" && value !== "undefined")
      .join(" • ")
    
    return attributeValues
  }, [itemAtributoNoEliminados])

  // ✅ OPTIMIZADO: Generar color gradiente con useCallback
  const generarColorGradiente = useCallback((porcentaje) => {
    // Normalizar el porcentaje entre 0 y 100
    const porcentajeNormalizado = Math.max(0, Math.min(100, porcentaje))
    
    // Definir colores del gradiente
    const colores = {
      muyBajo: '#ff4444',    // Rojo - Pérdida o muy baja ganancia
      bajo: '#ff8800',       // Naranja - Ganancia baja
      medio: '#ffcc00',      // Amarillo - Ganancia media
      bueno: '#88cc00',      // Verde claro - Buena ganancia
      excelente: '#44aa44'   // Verde - Excelente ganancia
    }
    
    // Definir rangos de porcentaje
    if (porcentajeNormalizado < 0) return colores.muyBajo
    if (porcentajeNormalizado < 10) return colores.bajo
    if (porcentajeNormalizado < 20) return colores.medio
    if (porcentajeNormalizado < 30) return colores.bueno
    return colores.excelente
  }, [])

  const fetchInitialData = async () => {
    try {
      await getItemAtributoNoEliminados()
    } catch (error) {}
  }

  // ✅ OPTIMIZADO: Crear caché compartido a nivel de componente para persistir entre renders
  const cacheMontos = useRef({})
  
  // ✅ OPTIMIZADO: Los datos financieros ya vienen calculados del backend usando BOM
  // No es necesario recalcular, solo retornar las recetas tal como vienen
  const cargarDatosFinancieros = useCallback(async (recetas) => {
    // El backend ya calculó precioPaquete, costoTotalServicios, ganancia, etc. usando BOM
    return recetas
  }, [])

  // ✅ OPTIMIZADO: Calcular totales con useCallback
  const calcularTotalesTodasLasPaginas = useCallback(async (idReceta, idMateriaPrima, idDestino, startDate, endDate, cerrado = null, reqId = null) => {
    try {
      const response = await getTablaRecetaTotales(
        startDate,
        endDate,
        idReceta,
        idMateriaPrima,
        idDestino,
        null,
        null,
        cerrado
      )

      const sumaPrecioOriginal = Number(response?.totalPrecioOriginal || 0)
      const sumaPrecioTotal = Number(response?.totalPrecioTotal || 0)
      const sumaCostoTotal = Number(response?.totalCostoTotal || 0)
      const sumaGanancia = Number(response?.totalGanancia || 0)

      // Evitar que una respuesta vieja pise a una nueva
      if (reqId !== null && reqId !== totalesReqIdRef.current) return

      setTotalPrecioOriginal(sumaPrecioOriginal)
      setTotalPrecioTotal(sumaPrecioTotal)
      setTotalCostoTotal(sumaCostoTotal)
      setTotalGanancia(sumaGanancia)
    } catch (error) {
      console.error("Error calculando totales:", error)
      if (reqId !== null && reqId !== totalesReqIdRef.current) return
      setTotalPrecioOriginal(0)
      setTotalPrecioTotal(0)
      setTotalCostoTotal(0)
      setTotalGanancia(0)
    } finally {
      if (reqId !== null && reqId !== totalesReqIdRef.current) return
      setLoadingTotales(false)
    }
  }, [getTablaRecetaTotales])

  // Listado ligero: sin ingredientes ni financieros (se rellenan después con getTablaRecetaFinancieros)
  const transformarRecetasLigero = useCallback((recetasData) => {
    return (recetasData || []).map((receta) => {
      const idPaq = receta.idPaquete || receta.id
      return {
        id: receta.id,
        idPaquete: idPaq,
        descripcionProductoTerminado: receta.descripcionProductoTerminado || "Sin descripción",
        codigoProductoTerminado: receta.codigoProductoTerminado || "Sin código",
        idProductoTerminado: receta.idProductoTerminado,
        destino: (receta.ubicacion?.descripcion || receta.destino) ?? "Sin destino",
        destinoId: receta.destino ?? receta.destinoId,
        fechaInicio: receta.fechaInicio ? dayjs(receta.fechaInicio).format("DD/MM/YYYY") : "N/A",
        fechaFin: receta.fechaFin ? dayjs(receta.fechaFin).format("DD/MM/YYYY") : "N/A",
        fechaInicioRaw: receta.fechaInicio ?? null,
        fechaFinRaw: receta.fechaFin ?? null,
        noches: receta.noches || 0,
        cerrado: receta.cerrado || false,
        cancelado: receta.cancelado || false,
        idVendedor: receta.idVendedor ?? null,
        vendedor: receta.vendedor ?? null,
        totalIngredientes: 0,
        cantidadTotal: "0",
        ingredientes: [],
        nombresIngredientes: "",
        fechaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("DD/MM/YYYY") : "N/A",
        fechaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("DD/MM/YYYY") : "N/A",
        horaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("HH:mm") : "N/A",
        horaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("HH:mm") : "N/A",
        primerIngrediente: "-",
        codigoPrimerIngrediente: "",
        precioPaquete: Number(receta.monto ?? 0) || 0,
        monto: receta.monto ?? 0,
        costoTotalServicios: 0,
        ganancia: 0,
        porcentajeGanancia: 0,
        pasajeros: 0,
        precioOriginal: 0,
        precioPorPasajeros: 0,
      }
    })
  }, [])

    // ✅ OPTIMIZADO: fetchData con useCallback
    const fetchData = useCallback(async (
      idReceta,
      idMateriaPrima,
      idDestino,
      page,
      limit = 100,
      fechaViajeDesde = null,
      fechaViajeHasta = null,
      fechaInicio = null,
      cerrado = null,
      cancelado = null,
      sortFieldParam = null,
      sortOrderParam = null
    ) => {
    try {
      setLoading(true)

      
// ?? FECHAS DE VIAJE: Normalizar si existen (fechaInicio del paquete)
      let dateViajeDesde = null
      let dateViajeHasta = null

      if (fechaViajeDesde) {
        const formattedViajeDesde = fechaViajeDesde.format("YYYY-MM-DD")
        const resultViajeDesde = await getFechaHoraActualClient(formattedViajeDesde)
        dateViajeDesde = dayjs(resultViajeDesde.fechaServidor).startOf("day").toDate()
      }

      if (fechaViajeHasta) {
        const formattedViajeHasta = fechaViajeHasta.format("YYYY-MM-DD")
        const resultViajeHasta = await getFechaHoraActualClient(formattedViajeHasta)
        dateViajeHasta = dayjs(resultViajeHasta.fechaServidor).endOf("day").toDate()
      }

      // ?? FECHA DE INICIO: Normalizar si existe
      let dateInicio = null
      if (fechaInicio) {
        const formattedInicio = fechaInicio.format("YYYY-MM-DD")
        const resultInicio = await getFechaHoraActualClient(formattedInicio)
        // Para igualdad exacta, necesitamos rango del día completo
        dateInicio = dayjs(resultInicio.fechaServidor).startOf("day").toDate()
      }

       // Paso 1: listado ligero (sin BOM ni financieros)
       const activeSort = Array.isArray(sortModel) && sortModel.length ? sortModel[0] : DEFAULT_SORT[0]
       const sortField = sortFieldParam || activeSort?.field || "fechaInicio"
       const sortOrder = sortOrderParam || (activeSort?.sort === "asc" ? "asc" : "desc")
 
      const response = await getTablaRecetaDatos(
        dateViajeDesde,
        dateViajeHasta,
        idReceta,
        idMateriaPrima,
        idDestino,
        page,
        limit,
        null,
        null,
        dateInicio,
        cerrado,
        cancelado,
        true, // soloListado
        sortField,
        sortOrder
      )

      let recetasData = []
      if (response.data) {
        setTotalPages(response.totalPages || 0)
        setTotalCount(response.total || 0)
        recetasData = response.data
      } else {
        recetasData = response
      }

      const recetasTransformadas = transformarRecetasLigero(recetasData)
      
      // ✅ OPTIMIZADO: Mostrar datos ligeros inmediatamente (1 render inicial)
      setFilteredRecetas(recetasTransformadas)
      setRecetas(recetasTransformadas)

      // Paso 2: financieros y totales EN PARALELO
      // Antes: financieros y luego totales (secuencial ~400-600ms)
      // Ahora: ambos en paralelo (~200-300ms)
      const idPaquetes = recetasData.map((r) => r.id || r.idPaquete).filter(Boolean)
      const reqId = ++totalesReqIdRef.current
      setLoadingTotales(true)
      
      // Ejecutar financieros y totales en paralelo
      const financierosPromise = idPaquetes.length > 0 
        ? getTablaRecetaFinancieros(idPaquetes).catch(err => {
            console.error("Error cargando financieros:", err)
            return { financieros: {} }
          })
        : Promise.resolve({ financieros: {} })
      
      const totalesPromise = calcularTotalesTodasLasPaginas(
        idReceta,
        idMateriaPrima,
        idDestino,
        dateViajeDesde,
        dateViajeHasta,
        cerrado,
        reqId
      )
      
      // Esperar financieros (totales se calculan en background y actualizan estado directamente)
      const { financieros } = await financierosPromise
      
      // ✅ OPTIMIZADO: Una sola actualización de estado con datos financieros
      // Antes: 4 setState separados (4 re-renders)
      // Ahora: función de actualización única que aplica a ambos estados
      if (Object.keys(financieros).length > 0) {
        const enriquecerConFinancieros = (recetas) =>
          recetas.map((r) => {
            const fin = financieros[r.id] || financieros[r.idPaquete]
            if (!fin) return r
            return {
              ...r,
              pasajeros: Number(fin.pasajeros || 0),
              precioPaquete: Number(fin.precioPaquete || 0),
              monto: r.monto ?? fin.precioPaquete,
              costoTotalServicios: Number(fin.costoTotalServicios || 0),
              ganancia: Number(fin.ganancia || 0),
              porcentajeGanancia: Number(fin.porcentajeGanancia || 0),
              precioOriginal: Number(fin.precioOriginal || 0),
              precioPorPasajeros: Number(fin.precioPorPasajeros || 0),
              totalIngredientes: Number(fin.cantidadServicios ?? 0),
            }
          })
        
        // Aplicar enriquecimiento a ambos estados
        const recetasEnriquecidas = enriquecerConFinancieros(recetasTransformadas)
        setRecetas(recetasEnriquecidas)
        setFilteredRecetas(recetasEnriquecidas)
      }
      
      // Nota: totales se actualizan en calcularTotalesTodasLasPaginas via setLoadingTotales
    } catch (error) {
      console.error("Error fetching recetas:", error)
    } finally {
      setLoading(false)
    }
  }, [
    getFechaHoraActualClient,
    getTablaRecetaDatos,
    getTablaRecetaFinancieros,
    transformarRecetasLigero,
    calcularTotalesTodasLasPaginas,
    sortModel,
  ])

  // Campos cuyos valores son calculados en el front (no existen en la BD)
  const COMPUTED_SORT_FIELDS = new Set([
    "precioOriginal",
    "precioPorPasajeros",
    "costoTotalServicios",
    "ganancia",
    "porcentajeGanancia",
  ])

  const handleExportVerBalancePdf = async () => {
    if (loading || isGeneratingPdf) return
    const deliveryContext = { targetWindow: reservePdfTarget() }

    try {
      setIsGeneratingPdf(true)

      const idReceta = recetaSelected ? recetaSelected.id : null
      const idDestino = destinoSelected ? destinoSelected.id : null
      let cerradoParam = null
      let canceladoParam = null
      if (filtroCerrado) cerradoParam = true
      else if (filtroAbierto) cerradoParam = false
      else if (filtroCancelado) canceladoParam = true

      const activeSort = Array.isArray(sortModel) && sortModel.length ? sortModel[0] : DEFAULT_SORT[0]
      const requestedSortField = activeSort?.field || "fechaInicio"
      const normalizedSortOrder = activeSort?.sort === "asc" ? "asc" : "desc"
      const backendSortField = COMPUTED_SORT_FIELDS.has(requestedSortField) ? "fechaInicio" : requestedSortField

      let dateViajeDesde = null
      let dateViajeHasta = null
      let dateInicio = null

      if (fechaViajeDesde) {
        const formattedViajeDesde = fechaViajeDesde.format("YYYY-MM-DD")
        const resultViajeDesde = await getFechaHoraActualClient(formattedViajeDesde)
        dateViajeDesde = dayjs(resultViajeDesde.fechaServidor).startOf("day").toDate()
      }

      if (fechaViajeHasta) {
        const formattedViajeHasta = fechaViajeHasta.format("YYYY-MM-DD")
        const resultViajeHasta = await getFechaHoraActualClient(formattedViajeHasta)
        dateViajeHasta = dayjs(resultViajeHasta.fechaServidor).endOf("day").toDate()
      }

      if (fechaInicioSelected) {
        const formattedInicio = fechaInicioSelected.format("YYYY-MM-DD")
        const resultInicio = await getFechaHoraActualClient(formattedInicio)
        dateInicio = dayjs(resultInicio.fechaServidor).startOf("day").toDate()
      }

      const cargarPaginaParaPdf = async (pageToLoad) => {
        const response = await getTablaRecetaDatos(
          dateViajeDesde,
          dateViajeHasta,
          idReceta,
          null,
          idDestino,
          pageToLoad,
          limit,
          null,
          null,
          dateInicio,
          cerradoParam,
          canceladoParam,
          true,
          backendSortField,
          normalizedSortOrder
        )

        const rawRows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []

        const rowsBase = transformarRecetasLigero(rawRows)
        const idPaquetes = rawRows.map((r) => r.id || r.idPaquete).filter(Boolean)
        let financieros = {}

        if (idPaquetes.length > 0) {
          try {
            const finResponse = await getTablaRecetaFinancieros(idPaquetes)
            financieros = finResponse?.financieros || {}
          } catch (error) {
            console.error("Error cargando financieros para PDF de Ver Balance:", error)
          }
        }

        const rowsConFinancieros = rowsBase.map((row) => {
          const fin = financieros[row.id] || financieros[row.idPaquete]
          if (!fin) return row
          return {
            ...row,
            pasajeros: Number(fin.pasajeros || 0),
            precioPaquete: Number(fin.precioPaquete || 0),
            monto: row.monto ?? fin.precioPaquete,
            costoTotalServicios: Number(fin.costoTotalServicios || 0),
            ganancia: Number(fin.ganancia || 0),
            porcentajeGanancia: Number(fin.porcentajeGanancia || 0),
            precioOriginal: Number(fin.precioOriginal || 0),
            precioPorPasajeros: Number(fin.precioPorPasajeros || 0),
            totalIngredientes: Number(fin.cantidadServicios ?? 0),
            cantidadVendedoresBom: Number(fin.cantidadVendedores ?? 0),
            primerVendedorBom: fin.primerVendedorBom ?? null,
            vendedoresBomNombres: Array.isArray(fin.vendedoresBomNombres) ? fin.vendedoresBomNombres : [],
          }
        })

        return {
          rows: rowsConFinancieros,
          totalPages: Number(response?.totalPages || 1),
        }
      }

      const firstPage = await cargarPaginaParaPdf(1)
      let allRows = [...firstPage.rows]

      for (let currentPage = 2; currentPage <= firstPage.totalPages; currentPage += 1) {
        const pageData = await cargarPaginaParaPdf(currentPage)
        allRows = allRows.concat(pageData.rows)
      }

      if (COMPUTED_SORT_FIELDS.has(requestedSortField)) {
        const multiplier = normalizedSortOrder === "asc" ? 1 : -1
        allRows = [...allRows].sort((a, b) => {
          const aVal = Number(a?.[requestedSortField] ?? 0)
          const bVal = Number(b?.[requestedSortField] ?? 0)
          return (aVal - bVal) * multiplier
        })
      }

      if (!allRows.length) {
        Swal.fire({
          icon: "info",
          title: "Sin datos",
          text: "No hay registros para exportar con los filtros actuales.",
        })
        return
      }

      const logoBase64 = await fetchLogoTenant()

      generarPdfVerBalance({
        rows: allRows,
        filtros: {
          viajeDesde: fechaViajeDesde ? fechaViajeDesde.format("DD/MM/YYYY") : null,
          viajeHasta: fechaViajeHasta ? fechaViajeHasta.format("DD/MM/YYYY") : null,
          fechaInicio: fechaInicioSelected ? fechaInicioSelected.format("DD/MM/YYYY") : null,
          grupo: recetaSelected?.label || null,
          destino: destinoSelected?.label || null,
          estado: filtroCancelado ? "Cancelado" : filtroCerrado ? "Cerrado" : filtroAbierto ? "Abierto" : null,
          orden: `${SORT_FIELD_LABELS[requestedSortField] || requestedSortField} (${normalizedSortOrder.toUpperCase()})`,
        },
        logoBase64,
        deliveryContext,
      })
    } catch (error) {
      console.error("Error exportando PDF de Ver Balance:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo generar el PDF. Intente nuevamente.",
      })
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleSortModelChange = (newModel) => {
    const nextSort = Array.isArray(newModel) && newModel.length ? newModel[0]?.sort : "desc"
    const normalizedSort = nextSort === "asc" ? "asc" : "desc"
    const clickedField = Array.isArray(newModel) && newModel.length ? newModel[0]?.field : "fechaInicio"
    const nextModel = [{ field: clickedField, sort: normalizedSort }]
    setSortModel(nextModel)

    // Para campos calculados: ordenar en memoria sin llamar al servidor
    if (COMPUTED_SORT_FIELDS.has(clickedField)) {
      setFilteredRecetas((prev) => {
        const sorted = [...prev].sort((a, b) => {
          const aVal = Number(a[clickedField] ?? 0)
          const bVal = Number(b[clickedField] ?? 0)
          return normalizedSort === "asc" ? aVal - bVal : bVal - aVal
        })
        return sorted
      })
      return
    }

    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    let canceladoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    else if (filtroCancelado) canceladoParam = true

    setPage(1)
    fetchData(
      idReceta,
      null,
      idDestino,
      1,
      limit,
      fechaViajeDesde,
      fechaViajeHasta,
      fechaInicioSelected,
      cerradoParam,
      canceladoParam,
      clickedField,
      normalizedSort
    )
  }

  useEffect(() => {
    const inicializar = async () => {
      // Inicializar fechas de viaje con el año actual (1/1 a 31/12)
      const today = dayjs()
      const fechaDesde = today.startOf("year")
      const fechaHasta = today.endOf("year")
      
      setFechaViajeDesde(fechaDesde)
      setFechaViajeHasta(fechaHasta)
      
      // ✅ OPTIMIZADO: Ejecutar llamadas independientes en paralelo
      // Antes: 3 llamadas secuenciales (~600-900ms)
      // Ahora: 3 llamadas paralelas (~200-300ms)
      const [, , parametrosRes] = await Promise.all([
        getUbicacion(),
        getItemAtributoNoEliminados(),
        getParametrosGlobales(["paquetesOcultarCantidadYCostoXCantidad"]),
      ])
      
      // Setear parámetro de configuración obtenido en paralelo
      if (parametrosRes) {
        const raw = parametrosRes.paquetesOcultarCantidadYCostoXCantidad
        const parsed = raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "TRUE"
        setPaquetesOcultarCantidadFlag(Boolean(parsed))
      }
      
      // Cargar datos iniciales con las fechas por defecto
      fetchData(null, null, null, 1, limit, fechaDesde, fechaHasta, null, null)
    }
    
    inicializar()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Solo al montar; funciones del contexto no son estables y provocan bucle infinito
  }, [])

  const refetchTablaReceta = useCallback(() => {
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) {
      cerradoParam = true
    } else if (filtroAbierto) {
      cerradoParam = false
    }

    fetchData(
      idReceta,
      null,
      idDestino,
      page,
      limit,
      fechaViajeDesde,
      fechaViajeHasta,
      fechaInicioSelected,
      cerradoParam
    )
  }, [
    recetaSelected,
    destinoSelected,
    filtroCerrado,
    filtroAbierto,
    page,
    limit,
    fechaViajeDesde,
    fechaViajeHasta,
    fechaInicioSelected,
    fetchData,
  ])


  // ✅ OPTIMIZADO: transformarRecetas con useCallback (para respuestas completas, p. ej. sin soloListado)
  const transformarRecetas = useCallback((recetasData) => {
    return recetasData.map((receta) => {
      const totalIngredientes = receta.ingredientes?.length || 0
      const cantidadTotal = receta.ingredientes?.reduce((sum, ing) => sum + (ing.cantidad || 0), 0) || 0

      // Obtener nombres de ingredientes para búsqueda
      const nombresIngredientes =
        receta.ingredientes?.map((ing) => ing.materiaPrima?.descripcion || "").join(", ") || ""

      return {
        id: receta.id,
        idPaquete: receta.idPaquete || receta.id, // ID del paquete (cabecera)
        descripcionProductoTerminado: receta.descripcionProductoTerminado || "Sin descripción",
        codigoProductoTerminado: receta.codigoProductoTerminado || "Sin código",
        idProductoTerminado: receta.idProductoTerminado,
        destino: receta.ubicacion?.descripcion || "Sin destino",
        destinoId: receta.destino || receta.destinoId, // ID del destino para cálculos
        fechaInicio: receta.fechaInicio ? dayjs(receta.fechaInicio).format("DD/MM/YYYY") : "N/A",
        fechaFin: receta.fechaFin ? dayjs(receta.fechaFin).format("DD/MM/YYYY") : "N/A",
        noches: receta.noches || 0,
        cerrado: receta.cerrado || false,
        cancelado: receta.cancelado || false,
        idVendedor: receta.idVendedor || null, // Vendedor del paquete
        vendedor: receta.vendedor || null, // Info del vendedor
        totalIngredientes,
        cantidadTotal: cantidadTotal.toFixed(2),
        ingredientes: receta.ingredientes || [],
        nombresIngredientes,
        // Agregar fechas formateadas
        fechaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("DD/MM/YYYY") : "N/A",
        fechaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("DD/MM/YYYY") : "N/A",
        horaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("HH:mm") : "N/A",
        horaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("HH:mm") : "N/A",
        // Para mostrar el primer ingrediente como referencia
        primerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.descripcion || "Sin ingredientes",
        codigoPrimerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.codigo || "",
        // Campos financieros (vienen precalculados desde backend)
        precioPaquete: Number(receta.precioPaquete || 0),
        // monto viene desde Paquete.monto; fallback a precioPaquete si no existe
        monto: receta.monto ?? receta.precioPaquete ?? 0,
        costoTotalServicios: Number(receta.costoTotalServicios || 0),
        ganancia: Number(receta.ganancia || 0),
        porcentajeGanancia: Number(receta.porcentajeGanancia || 0),
        pasajeros: Number(receta.pasajeros || 0),
        precioOriginal: Number.isFinite(Number(receta.precioOriginal))
        ? Number(receta.precioOriginal)
        : Number(receta.precioPaquete || 0) * Number(receta.pasajeros || 0),
        precioPorPasajeros: Number(receta.precioPorPasajeros || 0),
      }
    })
  }, [])

  // ✅ OPTIMIZADO: Función para cargar precios y costos con useCallback (MOVIDA ANTES DE handleOpenModal)
  const cargarPreciosCostos = useCallback(async (receta) => {
    try {
      // Cargar entidades una sola vez
      const proveedores = await getEntidadFilterTipoEntidad(2)
      // Precio del paquete: prioridad a cabecera (monto), fallback a precioPaquete
      const precioPaqueteValue = Number(receta.monto ?? receta.precioPaquete ?? 0)
      setPrecioPaquete(Number.isFinite(precioPaqueteValue) ? precioPaqueteValue : 0)

      // Cargar costos de servicios desde BOM (datos históricos)
      if (proveedores && proveedores.length > 0) {
        const nuevosPreciosCostos = {}
        receta.ingredientes.forEach((servicio) => {
          const key = `${servicio.idMateriaPrima}-${receta.destino}`
          // El monto en BOM es el costo TOTAL, necesitamos el costo UNITARIO para el modal
          const montoTotal = Number.parseFloat(servicio.monto || 0)
          const cantidad = Number.parseFloat(servicio.cantidad || 1)
          const costoUnitario = cantidad > 0 ? montoTotal / cantidad : montoTotal
          const idEntidadCosto = servicio.idEntidad || proveedores[0].id || null
          
          nuevosPreciosCostos[key] = { costo: costoUnitario, idEntidadCosto }
        })
        
        setPreciosCostos(nuevosPreciosCostos)
      }
    } catch (error) {
      console.error("Error cargando precios y costos:", error)
    }
  }, [getEntidadFilterTipoEntidad])

  const handleOpenModal = useCallback(async (receta) => {
    let recetaConIngredientes = receta
    if (!receta.ingredientes || receta.ingredientes.length === 0) {
      try {
        const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
        const ingredientes = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
        recetaConIngredientes = { ...receta, ingredientes }
      } catch (err) {
        console.error("Error cargando ingredientes del paquete:", err)
        recetaConIngredientes = { ...receta, ingredientes: [] }
      }
    }
    setSelectedReceta(recetaConIngredientes)
    setEditedIngredientes((recetaConIngredientes.ingredientes || []).map((ing) => ({ ...ing })))

    setOpenModal(true)
    setCargandoDatosModal(true)
    setTimeout(async () => {
      await cargarPreciosCostos(recetaConIngredientes)
      setCargandoDatosModal(false)
    }, 100)
  }, [cargarPreciosCostos, getBomByIdItem])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
    setSelectedReceta(null)
    setEditedIngredientes([])
    setPreciosCostos({})
    setPrecioPaquete(0)
    setCargandoDatosModal(false)
  }, [])

  // ✅ OPTIMIZADO: Funciones para modal de pasajeros con useCallback
  

  

  // Función para generar PDF de pasajeros
  const generatePassengersPDF = async (receta) => {
    try {
      const deliveryContext = { targetWindow: reservePdfTarget() }
      // Mostrar indicador de carga
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // BOM bajo demanda: si la fila no trae ingredientes (listado ligero), cargarlos antes de generar el PDF
      let recetaConIngredientes = receta
      if (!receta.ingredientes?.length) {
        try {
          const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
          const ingredientes = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
          recetaConIngredientes = { ...receta, ingredientes }
        } catch (err) {
          console.error("Error cargando ingredientes para PDF:", err)
          recetaConIngredientes = { ...receta, ingredientes: [] }
        }
      }

      // Obtener pasajeros
      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )
      const pasajerosOrdenados = sortPasajerosPdf(pasajerosData)

      Swal.close()

      if (!pasajerosOrdenados.length) {
        await Swal.fire({
          title: 'Sin pasajeros',
          text: 'No hay pasajeros registrados para este viaje',
          icon: 'info',
          confirmButtonText: 'Aceptar'
        })
        return
      }

      const { logoTenant, showFechaSalida } = await fetchPdfConfig()

      // Crear PDF
      const doc = new jsPDF({ unit: "pt", format: "a4" })

      // Paleta de colores
      const PAL = {
        primary: [25, 118, 210],
        secondary: [120, 144, 156],
        success: [76, 175, 80],
        warning: [255, 152, 0],
        error: [244, 67, 54],
        ink: [33, 33, 33],
        muted: [117, 117, 117],
        inkLight: [158, 158, 158],
        bgSoft: [245, 247, 250],
      }
      const M = { l: 30, r: 30, t: 40, b: 40 }

      // Helpers
      const text = (str, x, y, { size = 10, color = PAL.ink, align = "left", bold = false } = {}) => {
        doc.setFont("helvetica", bold ? "bold" : "normal")
        doc.setFontSize(size)
        doc.setTextColor(...color)
        doc.text(str, x, y, { align })
      }

      const badge = (label, x, y, color = PAL.primary) => {
        doc.setFont("helvetica", "bold")
        doc.setFontSize(9)
        const padX = 8
        const w = doc.getTextWidth(label) + padX * 2
        const h = 16
        doc.setFillColor(...color)
        doc.roundedRect(x, y - h + 2, w, h, 8, 8, "F")
        doc.setTextColor(255, 255, 255)
        doc.text(label, x + padX, y - 5)
        doc.setTextColor(...PAL.ink)
      }

      const sectionTitle = (label, y) => {
        doc.setDrawColor(...PAL.primary)
        doc.setLineWidth(1)
        doc.line(M.l, y + 6, doc.internal.pageSize.getWidth() - M.r, y + 6)
        text(label, M.l, y, { size: 12, color: PAL.primary, bold: true })
      }

      const kvRow = (label, value) => [
        { content: label, styles: { halign: "left", fontStyle: "bold", textColor: PAL.muted } },
        { content: value ?? "N/A", styles: { halign: "left" } },
      ]

      const formatCurrency = (value) => {
        return new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          maximumFractionDigits: 0,
        }).format(value)
      }

      const getImageFormat = (dataUrl) => {
        const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
        if (!match) return "PNG"
        const ext = match[1].toLowerCase()
        if (ext === "jpg" || ext === "jpeg") return "JPEG"
        if (ext === "webp") return "WEBP"
        return "PNG"
      }

      const resolveSaldo = (pasajero) => {
        const saldoPendiente = pasajero?.montoPendiente
        if (saldoPendiente !== null && saldoPendiente !== undefined && Number.isFinite(Number(saldoPendiente))) {
          return Number(saldoPendiente)
        }
        return 0
      }

      const resolveAbonado = (pasajero) => {
        const abonado = pasajero?.abonado
        if (abonado !== null && abonado !== undefined && Number.isFinite(Number(abonado))) {
          return Number(abonado)
        }
        return 0
      }

      const resolveTotal = (pasajero) => {
        const total = pasajero?.total
        if (total !== null && total !== undefined && Number.isFinite(Number(total))) {
          return Number(total)
        }
        return resolveAbonado(pasajero) + resolveSaldo(pasajero)
      }

      let proveedoresList = []
      try {
        const out = await getEntidadFilterTipoEntidad(2)
        proveedoresList = Array.isArray(out) ? out : []
      } catch (error) {
        console.error("Error cargando proveedores para PDF:", error)
      }
      const toEntidadKey = (value) => {
        if (value === null || value === undefined) return null
        return String(value)
      }
      const proveedoresMap = new Map(
        proveedoresList.map((prov) => [toEntidadKey(prov.id), prov])
      )
      const vendedoresDetalle = []
      const vendedoresSeen = new Set()
      ;(recetaConIngredientes.ingredientes || []).forEach((servicio) => {
        if (servicio?.esVendedor !== true) return
        const idEntidad = servicio?.idEntidad ?? servicio?.entidad?.id ?? null
        const entidadKey = toEntidadKey(idEntidad)
        const proveedor = entidadKey ? proveedoresMap.get(entidadKey) : null
        const nombre = [
          servicio?.entidad?.descripcion ?? proveedor?.descripcion,
          servicio?.entidad?.apellido ?? proveedor?.apellido,
        ]
          .filter(Boolean)
          .join(" ")
          .trim()
        if (!nombre) return
        const telefonoRaw = proveedor?.telefono ?? servicio?.entidad?.telefono ?? null
        const telefono = telefonoRaw == null ? "" : String(telefonoRaw).trim()
        const dedupeKey = `${entidadKey ?? "sin-id"}|${nombre}|${telefono}`
        if (vendedoresSeen.has(dedupeKey)) return
        vendedoresSeen.add(dedupeKey)
        vendedoresDetalle.push({ nombre, telefono })
      })
      const vendedoresBomFallback = Array.isArray(recetaConIngredientes?.vendedoresBomNombres)
        ? recetaConIngredientes.vendedoresBomNombres
            .map((nombre) => (typeof nombre === "string" ? nombre.trim() : ""))
            .filter(Boolean)
        : []
      const vendedoresHeaderLineas = vendedoresDetalle.length
        ? vendedoresDetalle.map((v) => (v.telefono ? `${v.nombre}  ${v.telefono}` : v.nombre))
        : vendedoresBomFallback.length
          ? vendedoresBomFallback
          : [getVendedoresTexto(recetaConIngredientes)]

      // Header
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()

      if (logoTenant) {
        try {
          const logoMaxW = 90
          const logoMaxH = 42
          const props = doc.getImageProperties(logoTenant)
          const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
          const logoW = props.width * ratio
          const logoH = props.height * ratio
          doc.addImage(logoTenant, getImageFormat(logoTenant), M.l, 14, logoW, logoH)
        } catch (error) {
          // Ignorar errores de logo para no bloquear el PDF
        }
      }

      text("LISTA DE PASAJEROS", pageWidth / 2, 38, {
        size: 16,
        color: PAL.primary,
        align: "center",
        bold: true,
      })

      text(`Fecha de emisión: ${new Date().toLocaleDateString("es-AR")}`, pageWidth - M.r, 58, {
        size: 10,
        color: PAL.inkLight,
        align: "right",
      })

      text("Vendedor", pageWidth - M.r, 70, {
        size: 10,
        color: PAL.inkLight,
        align: "right",
      })
      const vendedorBaseY = 82
      vendedoresHeaderLineas.forEach((linea, index) => {
        text(linea || "-", pageWidth - M.r, vendedorBaseY + index * 11, {
          size: 10,
          color: PAL.ink,
          align: "right",
        })
      })

      // Sección 1: Información del Viaje
      const ultimoRenglonVendedorY = vendedorBaseY + (Math.max(vendedoresHeaderLineas.length, 1) - 1) * 11
      let cursorY = Math.max(120, ultimoRenglonVendedorY + 24)
      sectionTitle("INFORMACIÓN DEL VIAJE", cursorY)
      cursorY += 18

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "plain",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, textColor: PAL.ink },
        columnStyles: { 0: { cellWidth: 140 } },
        body: [
          kvRow("Grupo", `${receta.codigoProductoTerminado || ""} - ${receta.descripcionProductoTerminado || ""}`),
          kvRow("Destino", receta.destino || "N/A"),
          ...(showFechaSalida
            ? [
                kvRow("Fecha de inicio de viaje", receta.fechaInicio || "N/A"),
                kvRow("Fecha de fin", receta.fechaFin || "N/A"),
              ]
            : []),
          kvRow("Noches", String(receta.noches || 0)),
        ],
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Sección 2: Tabla de Pasajeros
      sectionTitle("PASAJEROS", cursorY)
      cursorY += 20

      const pasajerosRows = pasajerosOrdenados.map((pasajero, index) => {
        const nombreCompleto = getPasajeroNombrePdf(pasajero)
        const total = resolveTotal(pasajero)
        const abonado = resolveAbonado(pasajero)
        const saldo = resolveSaldo(pasajero)
        
        return [
          String(index + 1),
          nombreCompleto,
          pasajero.dniCuitCuil || "-",
          pasajero.viaja !== false ? "Sí" : "No",
          pasajero.liberado === true ? "Sí" : "No",
          formatCurrency(total),
          formatCurrency(abonado),
          formatCurrency(saldo),
        ]
      })

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "striped",
        head: [["#", "Pasajero", "DNI/CUIT", "Viaja", "Liberado", "Total", "Abonado", "A Pagar"]],
        body: pasajerosRows,
        styles: { font: "helvetica", fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255], fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 25, halign: "center" },
          1: { cellWidth: 150 },
          2: { cellWidth: 70 },
          3: { cellWidth: 40, halign: "center" },
          4: { cellWidth: 45, halign: "center" },
          5: { cellWidth: 65, halign: "right" },
          6: { cellWidth: 65, halign: "right" },
          7: { cellWidth: 65, halign: "right" },
        },
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Sección 3: Servicios Asociados al Paquete
      sectionTitle("SERVICIOS ASOCIADOS AL PAQUETE", cursorY)
      cursorY += 20

      const serviciosRows = (recetaConIngredientes.ingredientes || []).map((servicio, index) => {
        const descripcion = servicio.materiaPrima?.descripcion || "Sin descripción"
        const proveedorKey = toEntidadKey(servicio.idEntidad)
        const proveedor = (proveedorKey ? proveedoresMap.get(proveedorKey) : null)?.descripcion || "-"
        const costo = Number(servicio.monto || 0)

        return [
          String(index + 1),
          descripcion,
          proveedor,
          formatCurrency(costo),
        ]
      })

      if (serviciosRows.length === 0) {
        text("No hay servicios asociados a este paquete.", M.l, cursorY, { size: 10, color: PAL.muted })
        cursorY += 20
      } else {
        doc.autoTable({
          startY: cursorY,
          margin: { left: M.l, right: M.r },
          theme: "striped",
          head: [["#", "Servicio", "Proveedor", "Costo"]],
          body: serviciosRows,
          styles: { font: "helvetica", fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255], fontStyle: "bold" },
          columnStyles: {
            0: { cellWidth: 25, halign: "center" },
            1: { cellWidth: 240 },
            2: { cellWidth: 140 },
            3: { cellWidth: 80, halign: "right" },
          },
        })

        cursorY = doc.lastAutoTable.finalY + 18
      }

      // Sección 4: Estadísticas
      sectionTitle("ESTADISTICAS", cursorY)
      cursorY += 18

      const costoTotalServicios = Number(recetaConIngredientes.costoTotalServicios ?? 0)
      const totalAbonado = Number(recetaConIngredientes.precioPorPasajeros ?? 0)
      const gananciaCalc = Number(recetaConIngredientes.ganancia ?? (totalAbonado - costoTotalServicios))

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "plain",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, textColor: PAL.ink },
        columnStyles: { 0: { cellWidth: 160 } },
        body: [
          kvRow("Total abonado", formatCurrency(totalAbonado)),
          kvRow("Total Costo", formatCurrency(costoTotalServicios)),
          kvRow("Ganancia", formatCurrency(gananciaCalc)),
        ],
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Footer
      const footerY = pageHeight - 70
      doc.setDrawColor(...PAL.secondary)
      doc.setLineWidth(0.4)
      doc.line(M.l, footerY, pageWidth - M.r, footerY)
      text("Documento informativo · No válido como comprobante fiscal", pageWidth / 2, footerY + 18, {
        size: 9,
        color: PAL.inkLight,
        align: "center",
      })
      text("Consultas: contactar al administrador del sistema.", pageWidth / 2, footerY + 32, {
        size: 9,
        color: PAL.inkLight,
        align: "center",
      })

      // Metadata
      doc.setProperties({
        title: `Pasajeros_${receta.codigoProductoTerminado}_${receta.destino}`,
        subject: "Lista de Pasajeros",
        author: "LUTENTE TOURS",
        creator: "LUTENTE TOURS",
      })

      const fileName = `pasajeros_${receta.codigoProductoTerminado}_${receta.destino}_${new Date().toISOString().slice(0, 10)}.pdf`
      deliverPdf({ doc, fileName, targetWindow: deliveryContext.targetWindow })

    } catch (error) {
      console.error("Error generando PDF de pasajeros:", error)
      Swal.close()
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo generar el PDF. Por favor, intente nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    }
  }

  const handleCantidadChange = useCallback((index, newCantidad) => {
    const updated = [...editedIngredientes]
    updated[index] = {
      ...updated[index],
      cantidad: Number.parseFloat(newCantidad) || 0,
    }
    setEditedIngredientes(updated)
  }, [editedIngredientes])

  const handleSaveChanges = useCallback(async () => {
  //console.log("Receta seleccionada", selectedReceta);
  //console.log("Guardando cambios:", editedIngredientes);

  try {
    setLoading(true);

    const ingredientesConCantidad = editedIngredientes.map((item) => {
      return {
        idItem: item.idMateriaPrima,
        cantidad: item.cantidad || "0",
        idUbicacion: item.idUbicacion || 1,
      };
    });

    const idProductoTerminado = selectedReceta.idProductoTerminado;
    const destino = selectedReceta.destinoId ?? selectedReceta.destino;
    const idPaquete = selectedReceta.idPaquete ?? selectedReceta.id;
    await crearRecetaApi(
      idProductoTerminado,
      ingredientesConCantidad,
      true,
      destino,
      selectedReceta.fechaInicioRaw ?? selectedReceta.fechaInicio,
      selectedReceta.fechaFinRaw ?? selectedReceta.fechaFin,
      selectedReceta.noches ?? null,
      idPaquete,
      null,
      selectedReceta.monto ?? null
    );

  } catch (error) {
    console.error("Error creando o actualizando receta:", error);
  } finally {
    setLoading(false);
    handleCloseModal();
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam)
  }
  }, [editedIngredientes, selectedReceta, crearRecetaApi, handleCloseModal, recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, fetchData, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected])

  const limpiarFiltros = useCallback(() => {
    setSearchTextReceta("")
    setRecetaSelected(null)
    setDestinoSelected(null)
    setFiltroProducto("")
    setFiltroDestino("")
    const today = dayjs()
    // Reinicializar fechas de viaje con el año actual (1/1 a 31/12)
    setFechaViajeDesde(today.startOf("year"))
    setFechaViajeHasta(today.endOf("year"))
    // Limpiar filtro de fecha inicio
    setFechaInicioSelected(null)
    // Limpiar filtros de estado
    setFiltroCerrado(false)
    setFiltroAbierto(false)
    setFiltroCancelado(false)
  }, [])

  const aplicarFiltros = useCallback(() => {
    //console.log(recetaSelected)
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    // Determinar el valor de cerrado y cancelado según los checkboxes
    let cerradoParam = null
    let canceladoParam = null
    if (filtroCerrado) {
      cerradoParam = true
    } else if (filtroAbierto) {
      cerradoParam = false
    } else if (filtroCancelado) {
      canceladoParam = true
    }
    // Al aplicar filtros, volver a la página 1
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
  }, [recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, filtroCancelado, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // 🔧 Manejar cambio de checkbox Cerrado (mutuamente excluyente)
  const handleFiltroCerradoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroCerrado(true)
      setFiltroAbierto(false) // Deseleccionar el otro
      setFiltroCancelado(false)
    } else {
      setFiltroCerrado(false) // Si se deselecciona el mismo, simplemente deseleccionar
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const cerradoParam = checked ? true : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, null)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // 🔧 Manejar cambio de checkbox Abierto (mutuamente excluyente)
  const handleFiltroAbiertoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroAbierto(true)
      setFiltroCerrado(false) // Deseleccionar el otro
      setFiltroCancelado(false)
    } else {
      setFiltroAbierto(false) // Si se deselecciona el mismo, simplemente deseleccionar
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const cerradoParam = checked ? false : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, null)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // 🔧 Manejar cambio de checkbox Cancelado (mutuamente excluyente)
  const handleFiltroCanceladoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroCancelado(true)
      setFiltroCerrado(false)
      setFiltroAbierto(false)
    } else {
      setFiltroCancelado(false)
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const canceladoParam = checked ? true : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, null, canceladoParam)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // 🔧 Toggle estado del paquete (sin guardar inmediatamente - batch)
  const handleToggleEstadoPaquete = useCallback((paquete) => {
    setCambiosPendientes(prev => {
      const newMap = new Map(prev)
      const idPaquete = paquete.idPaquete
      
      if (newMap.has(idPaquete)) {
        // Si ya está en cambios pendientes, verificar si vuelve al estado original
        const cambio = newMap.get(idPaquete)
        if (cambio.estadoOriginal === !paquete.cerrado) {
          // Vuelve al estado original, quitar de cambios pendientes
          newMap.delete(idPaquete)
        } else {
          // Actualizar estado nuevo
          newMap.set(idPaquete, {
            ...cambio,
            estadoNuevo: !paquete.cerrado
          })
        }
      } else {
        // Agregar nuevo cambio pendiente
        newMap.set(idPaquete, {
          estadoOriginal: paquete.cerrado,
          estadoNuevo: !paquete.cerrado,
          codigoProductoTerminado: paquete.codigoProductoTerminado,
          descripcionProductoTerminado: paquete.descripcionProductoTerminado,
          destino: paquete.destino,
          fechaInicio: paquete.fechaInicio,
          fechaFin: paquete.fechaFin,
          noches: paquete.noches,
          pasajeros: paquete.pasajeros,
          precioPorPasajeros: paquete.precioPorPasajeros,
          costoTotalServicios: paquete.costoTotalServicios,
          ganancia: paquete.ganancia
        })
      }
      
      return newMap
    })
  }, [])

  // Función para resaltar filas con cambios pendientes
  const getRowClassName = useCallback((params) => {
    if (params.row.cancelado === true) {
      return 'fila-cancelada'
    }
    if (cambiosPendientes.has(params.row.idPaquete)) {
      return 'fila-cambio-pendiente'
    }
    return ''
  }, [cambiosPendientes])

  // Función para generar PDF de cambios de estado
  const generarPDFCambiosEstado = useCallback(async (cambios) => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    const logoTenant = await fetchLogoTenant()
    // PDF en orientación horizontal para más columnas
    const doc = new jsPDF('landscape')
    const fecha = dayjs().format("DD/MM/YYYY HH:mm")
    
    // Configuración de colores y márgenes
    const PAL = {
      primary: [91, 91, 95],
      secondary: [244, 177, 14],
      success: [76, 175, 80],
      warning: [255, 128, 66],
      ink: [60, 60, 60],
      muted: [130, 130, 130]
    }
    const M = { l: 15, r: 15, t: 20, b: 20 }

    const getImageFormat = (dataUrl) => {
      const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
      if (!match) return "PNG"
      const ext = match[1].toLowerCase()
      if (ext === "jpg" || ext === "jpeg") return "JPEG"
      if (ext === "webp") return "WEBP"
      return "PNG"
    }
    
    // Separar cambios por tipo
    const cambiosCerrados = cambios.filter(c => c.estadoNuevo === true)
    const cambiosAbiertos = cambios.filter(c => c.estadoNuevo === false)
    
    // Header
    doc.setFillColor(...PAL.primary)
    doc.rect(0, 0, 297, 35, "F") // 297 = ancho en landscape
    if (logoTenant) {
      try {
        const logoMaxW = 50
        const logoMaxH = 20
        const props = doc.getImageProperties(logoTenant)
        const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
        const logoW = props.width * ratio
        const logoH = props.height * ratio
        doc.addImage(logoTenant, getImageFormat(logoTenant), M.l, 8, logoW, logoH)
      } catch (error) {
        // Ignorar errores de logo para no bloquear el PDF
      }
    }
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.setFont("helvetica", "bold")
    doc.text("CAMBIOS DE ESTADO DE PAQUETES", 148.5, 15, { align: "center" })
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(`Fecha de emisión: ${fecha}`, 148.5, 28, { align: "center" })
    
    let cursorY = 45
    
    // Información general
    doc.setTextColor(...PAL.ink)
    doc.setFontSize(11)
    doc.setFont("helvetica", "bold")
    doc.text(`Total de paquetes modificados: ${cambios.length}`, M.l, cursorY)
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(`Cerrados: ${cambiosCerrados.length}  |  Abiertos: ${cambiosAbiertos.length}`, M.l, cursorY + 6)
    cursorY += 16
    
    // Función helper para crear tabla
    const crearTabla = (datos, titulo, color) => {
      if (datos.length === 0) return
      
      // Título de sección
      doc.setFillColor(...color)
      doc.rect(M.l, cursorY, 267, 8, "F") // 267 = 297 - márgenes
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(10)
      doc.setFont("helvetica", "bold")
      doc.text(titulo, M.l + 3, cursorY + 5.5)
      cursorY += 10
      
      // Preparar datos con formato de moneda
      const tableData = datos.map((cambio, index) => [
        String(index + 1),
        cambio.codigoProductoTerminado,
        cambio.descripcionProductoTerminado,
        cambio.destino,
        cambio.fechaInicio || "-",
        cambio.fechaFin || "-",
        String(cambio.noches || 0),
        String(cambio.pasajeros || 0),
        formatearMoneda(cambio.precioPorPasajeros || 0),
        formatearMoneda(cambio.costoTotalServicios || 0),
        formatearMoneda(cambio.ganancia || 0)
      ])
      
      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        head: [["#", "Código", "Grupo", "Destino", "F. Inicio", "F. Fin", "Noches", "Pasaj.", "Precio", "Costo", "Ganancia"]],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: PAL.primary,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8
        },
        styles: {
          font: "helvetica",
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "linebreak"
        },
        columnStyles: {
          0: { cellWidth: 8, halign: "center" },     // #
          1: { cellWidth: 18, halign: "center" },    // Código
          2: { cellWidth: 50 },                      // Grupo
          3: { cellWidth: 35 },                      // Destino
          4: { cellWidth: 20, halign: "center" },    // F. Inicio
          5: { cellWidth: 20, halign: "center" },    // F. Fin
          6: { cellWidth: 14, halign: "center" },    // Noches
          7: { cellWidth: 14, halign: "center" },    // Pasajeros
          8: { cellWidth: 25, halign: "right" },     // Precio
          9: { cellWidth: 25, halign: "right" },     // Costo
          10: { cellWidth: 25, halign: "right" }     // Ganancia
        }
      })
      
      cursorY = doc.lastAutoTable.finalY + 12
      
      // Si se pasa de página, agregar espacio
      if (cursorY > 190) { // Ajustado para landscape
        doc.addPage()
        cursorY = 20
      }
    }
    
    // Tabla de paquetes CERRADOS
    if (cambiosCerrados.length > 0) {
      crearTabla(cambiosCerrados, `PAQUETES CERRADOS (${cambiosCerrados.length})`, PAL.success)
    }
    
    // Tabla de paquetes ABIERTOS
    if (cambiosAbiertos.length > 0) {
      crearTabla(cambiosAbiertos, `PAQUETES ABIERTOS (${cambiosAbiertos.length})`, PAL.warning)
    }
    
    // Footer en todas las páginas
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(...PAL.muted)
      doc.text(
        `Página ${i} de ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      )
    }
    
    // Guardar PDF
    const nombreArchivo = `cambios_estado_${dayjs().format("YYYY-MM-DD_HHmm")}.pdf`
    doc.setProperties({ title: nombreArchivo, subject: "Cambios de Estado", creator: "Sistema" })
    deliverPdf({ doc, fileName: nombreArchivo, targetWindow: deliveryContext.targetWindow })
  }, [])

  // Función para guardar cambios en batch
  const handleGuardarEstados = useCallback(async () => {
    if (cambiosPendientes.size === 0) return
    
    try {
      Swal.fire({
        title: `Guardando ${cambiosPendientes.size} cambio(s)...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })
      
      // Guardar todos los cambios en paralelo
      const promesas = Array.from(cambiosPendientes.entries()).map(([idPaquete, cambio]) => 
        setCerradoPaqueteApi(idPaquete, cambio.estadoNuevo)
      )
      
      await Promise.all(promesas)
      
      // Generar PDF con los cambios
      await generarPDFCambiosEstado(Array.from(cambiosPendientes.values()))
      
      Swal.fire({
        icon: 'success',
        title: 'Cambios guardados',
        text: `Se actualizaron ${cambiosPendientes.size} paquete(s). El PDF se ha descargado automáticamente.`,
        timer: 3000,
        showConfirmButton: false
      })
      
      // Limpiar cambios pendientes
      setCambiosPendientes(new Map())
      
      // Recargar tabla
      const idReceta = recetaSelected ? recetaSelected.id : null
      const idDestino = destinoSelected ? destinoSelected.id : null
      let cerradoParam = null
      if (filtroCerrado) cerradoParam = true
      else if (filtroAbierto) cerradoParam = false
      
      await fetchData(
        idReceta,
        null,
        idDestino,
        page,
        limit,
        fechaViajeDesde,
        fechaViajeHasta,
        fechaInicioSelected,
        cerradoParam
      )
      
    } catch (error) {
      console.error("Error al guardar cambios:", error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron guardar algunos cambios. Por favor, revise e intente nuevamente.'
      })
    }
  }, [cambiosPendientes, setCerradoPaqueteApi, recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData, generarPDFCambiosEstado])

  const hayFiltrosActivos = searchTextReceta || recetaSelected || destinoSelected || fechaViajeDesde || fechaViajeHasta || fechaInicioSelected || filtroCerrado || filtroAbierto || filtroCancelado

  // 🔧 CÁLCULOS FINANCIEROS
  const calcularCostoTotal = useMemo(() => {
    return editedIngredientes.reduce((total, servicio) => {
      const key = `${servicio.idMateriaPrima}-${selectedReceta?.destino}`
      const cantidad = Number.parseFloat(servicio.cantidad || 0)
      const costo = preciosCostos[key]?.costo || 0
      return total + (cantidad * costo)
    }, 0)
  }, [editedIngredientes, preciosCostos, selectedReceta?.destino])

  const calcularGanancia = useMemo(() => {
    return precioPaquete - calcularCostoTotal
  }, [precioPaquete, calcularCostoTotal])

  const calcularMargenGanancia = useMemo(() => {
    if (precioPaquete === 0) return 0
    return (calcularGanancia / precioPaquete) * 100
  }, [calcularGanancia, precioPaquete])

  const columns = [
    // ✅ COLUMNA 1: F. Inicio (VISIBLE)
    {
      field: "fechaInicio",
      headerName: "Fecha Inicio",
      flex: 0.4,
      minWidth: 120,
      headerAlign: "center",
      hide: false,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Inicio
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    // ✅ COLUMNA 2: Grupo (VISIBLE)
    {
      field: "codigoProductoTerminado",
      headerName: "Grupo",
      flex: 0.3,
      minWidth: 150,
      headerAlign: "center",
      align: "left",
      hide: false,
      sortingOrder: ["asc", "desc"],
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Grupo
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", justifyContent: "flex-start", flexWrap: "wrap" }}>
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              borderColor: "rgb(244, 147, 14)",
              color: "rgb(244, 147, 14)",
              borderRadius: 1,
              borderWidth: 1,
            }}
          />
        </Box>
      ),
    },
    // ✅ COLUMNA 3: Destino (VISIBLE)
    {
      field: "destino",
      headerName: "Destino",
      flex: 0.6,
      minWidth: 150,
      headerAlign: "center",
      align: "left",
      hide: false,
      sortingOrder: ["asc", "desc"],
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Destino
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LocationOnIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    // ✅ COLUMNA 4: Precio Original (controlada por permiso - no toggleable)
    {
      field: "precioOriginal",
      headerName: "Precio Original",
      flex: 0.4,
      minWidth: 130,
      headerAlign: "center",
      align: "right",
      hide: false,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Precio Original
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
            {formatearMoneda(params.value || 0)}
          </Typography>
        </Box>
      ),
    },
  // ✅ COLUMNA 5: Precio Total (controlada por permiso - no toggleable)
  {
    field: "precioPorPasajeros",
    headerName: "Precio Total",
    flex: 0.4,
    minWidth: 120,
    headerAlign: "center",
    align: "right",
    hide: false,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Precio Total
      </Typography>
    ),
    renderCell: (params) => (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", color: theme.palette.success.main }}>
          {formatearMoneda(params.value || 0)}
        </Typography>
      </Box>
    ),
  },
  // ✅ COLUMNA 6: Costo Total (controlada por permiso - no toggleable)
  {
    field: "costoTotalServicios",
    headerName: "Costo Total",
    flex: 0.4,
    minWidth: 120,
    headerAlign: "center",
    align: "right",
    hide: false,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Costo Total
      </Typography>
    ),
    renderCell: (params) => (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", color: theme.palette.warning.main }}>
          {formatearMoneda(params.value)}
        </Typography>
      </Box>
    ),
  },
  // ✅ COLUMNA 7: Ganancia (controlada por permiso - no toggleable)
  {
    field: "ganancia",
    headerName: "Ganancia",
    flex: 0.4,
    minWidth: 120,
    headerAlign: "center",
    align: "right",
    hide: false,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Ganancia
      </Typography>
    ),
    renderCell: (params) => {
      const ganancia = params.value || 0
      const color = ganancia >= 0 ? theme.palette.info.main : theme.palette.error.main
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", color }}>
            {formatearMoneda(ganancia)}
          </Typography>
        </Box>
      )
    },
  },
  // ✅ COLUMNA 8: % Ganancia (VISIBLE)
  {
    field: "porcentajeGanancia",
    headerName: "% Ganancia",
    flex: 0.4,
    minWidth: 120,
    headerAlign: "center",
    align: "right",
    hide: false,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        % Ganancia
      </Typography>
    ),
    renderCell: (params) => {
      const porcentaje = params.value || 0
      const colorGradiente = generarColorGradiente(porcentaje)
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              backgroundColor: colorGradiente,
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              minWidth: 80,
            }}
          >
            <PercentIcon fontSize="small" sx={{ color: "white" }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)"
              }}
            >
              {porcentaje.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      )
    },
  },
  // ✅ COLUMNA 9: Acciones (controlada por permiso - no toggleable)
  {
    field: "acciones",
    headerName: "Acciones",
    flex: 0.3,
    minWidth: 200,
    headerAlign: "center",
    sortable: false,
    hide: false,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Acciones
      </Typography>
    ),
    renderCell: (params) => (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, width: "100%", overflow: "visible" }}>
        {mostrarBtnEditarGrupo && (
          <Tooltip title="Editar Grupo">
            <IconButton
              size="small"
              onClick={() => handleOpenModalEdicion(params.row)}
              sx={{
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <EditIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
            </IconButton>
          </Tooltip>
        )}
        {mostrarBtnDescargarPdfPasajeros && (
          <Tooltip title="Descargar Lista de Pasajeros">
            <IconButton
              size="small"
              onClick={() => generatePassengersPDF(params.row)}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <PictureAsPdfIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  },
  // ⏸️ COLUMNAS OCULTAS (pueden activarse desde el menú de configuración)
  {
    field: "id",
    headerName: "ID",
    flex: 0.2,
    minWidth: 80,
    headerAlign: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        ID
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Chip label={params.value} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
      </Box>
    ),
  },
  {
    field: "fechaCreacion",
    headerName: "Fecha",
    flex: 0.35,
    minWidth: 110,
    headerAlign: "center",
    align: "left",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Fecha
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "fechaFin",
    headerName: "Fecha Fin",
    flex: 0.4,
    minWidth: 120,
    headerAlign: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        F. Fin
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: "cerrado",
    headerName: "Estado",
    flex: 0.35,
    minWidth: 140,
    headerAlign: "center",
    align: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Estado
      </Typography>
    ),
    renderCell: (params) => {
      const idPaquete = params.row.idPaquete
      const cambio = cambiosPendientes.get(idPaquete)
      const esCerrado = cambio ? cambio.estadoNuevo : params.value === true
      const esCancelado = params.row.cancelado === true
      const tieneCambios = cambiosPendientes.has(idPaquete)
      
      // Si está cancelado, mostrar solo el indicador de cancelado
      if (esCancelado) {
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Chip
              label="CANCELADO"
              size="small"
              color="error"
              sx={{ fontWeight: 700 }}
            />
          </Box>
        )
      }
      
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <Tooltip 
            title={esCerrado ? "Paquete cerrado. Click para abrir" : "Paquete abierto. Click para cerrar"} 
            arrow
          >
            <Checkbox
              checked={esCerrado}
              onChange={() => handleToggleEstadoPaquete(params.row)}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              sx={{
                color: theme.palette.warning.main,
                '&.Mui-checked': {
                  color: theme.palette.success.main,
                },
              }}
            />
          </Tooltip>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: esCerrado ? theme.palette.success.main : theme.palette.warning.main 
            }}
          >
            {esCerrado ? "Cerrado" : "Abierto"}
          </Typography>
          {tieneCambios && (
            <Tooltip title="Cambio pendiente" arrow>
              <Badge color="warning" variant="dot" />
            </Tooltip>
          )}
        </Box>
      )
    },
  },
  {
    field: "noches",
    headerName: "Noches",
    flex: 0.25,
    minWidth: 90,
    headerAlign: "center",
    align: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Noches
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Chip 
          label={params.value || 0} 
          size="small" 
          color="secondary" 
          variant="outlined" 
          sx={{ fontWeight: 600 }} 
        />
      </Box>
    ),
  },
  {
    field: "totalIngredientes",
    headerName: "Servicios",
    flex: 0.3,
    minWidth: 120,
    headerAlign: "center",
    align: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Servicios
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Tooltip 
          title={
            params.value > 0 
              ? `Click para ver ${params.value} servicio${params.value > 1 ? 's' : ''}`
              : "No hay servicios asociados"
          }
        >
          <Box
            onClick={() => params.value > 0 && handleOpenModalServicios(params.row)}
            sx={{
              cursor: params.value > 0 ? "pointer" : "default",
              "&:hover": params.value > 0 ? {
                transform: "scale(1.05)",
                transition: "transform 0.2s ease"
              } : {}
            }}
          >
            <Badge badgeContent={params.value} color="info">
              <BallotIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
            </Badge>
          </Box>
        </Tooltip>
      </Box>
    ),
  },
  {
    field: "pasajeros",
    headerName: "Pasajeros Confirmados",
    flex: 0.3,
    minWidth: 150,
    headerAlign: "center",
    align: "center",
    hide: true,
    renderHeader: () => (
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
        Pasajeros Confirmados
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        >
          <Tooltip
            title={
              params.value > 0
                ? `Click para ver ${params.value} pasajero${params.value > 1 ? "s" : ""}`
                : "No hay pasajeros confirmados"
            }
          >
            <Box
              onClick={() => params.value > 0 && handleOpenModalPasajerosConfirmados(params.row)}
              sx={{
                cursor: params.value > 0 ? "pointer" : "default",
                "&:hover": params.value > 0
                  ? { transform: "scale(1.05)", transition: "transform 0.2s ease" }
                  : {},
              }}
            >
              <Badge badgeContent={params.value || 0} color="info">
                <GroupsIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
              </Badge>
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "fechaActualizacion",
      headerName: "Fecha Actualización",
      flex: 0.3,
      minWidth: 130,
      hide: true,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Actualización
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            {params.row.horaActualizacion}
          </Typography>
        </Box>
      ),
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#f8f9fa",
          boxSizing: "border-box",
        }}
      >
        {/* Barra compacta para Laptop/Mobile: botón de Filtros + acciones clave */}
        {isLaptopDown && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 1,
              pt: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={aplicarFiltros}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                  color: "white",
                  "&:hover": { backgroundColor: alpha("#5b5b5f", 0.9) },
                }}
              >
                Buscar
              </Button>
              <Tooltip title="Crear nuevo paquete">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCrearPaqueteModal}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#5b5b5f",
                    "&:hover": { backgroundColor: alpha("#5b5b5f", 0.8) },
                  }}
                >
                  Crear
                </Button>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Contenedor de filtros (siempre visible en Desktop; colapsable en Laptop/Mobile) */}
        <Collapse in={filtersOpen || !isLaptopDown} timeout={250} unmountOnExit>
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2, 
              mb: 2, 
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}>
              {/* Encabezado en una sola fila (desktop). En pantallas chicas puede envolver. */}
              <Grid container spacing={1.5} alignItems="center" wrap="wrap">
                {/* Fecha Creación (Desde - Hasta) */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={fechaViajeDesde}
                        onChange={(newValue) => setFechaViajeDesde(newValue)}
                        format="DD/MM/YYYY"
                        label="Viaje Desde"
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={fechaViajeHasta}
                        onChange={(newValue) => setFechaViajeHasta(newValue)}
                        format="DD/MM/YYYY"
                        label="Viaje Hasta"
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>

                {/* Buscar Grupo */}
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={(itemTresLetras || []).map((it) => ({
                      label: generateRecetaLabel(it),
                      descripcion: it.descripcion,
                      codigo: it.codigo,
                      id: it.id,
                      codigoScanner: it.codigoScanner,
                    }))}
                    value={recetaSelected}
                    onChange={handleRecetaChange}
                    onInputChange={(event, newInputValue) => setSearchTextReceta(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar Grupo"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Buscar Destino (mismo ancho que Buscar Grupo) */}
                <Grid item xs={12} md={2}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={(ubicaciones || [])
                      .filter((ubicacion) => ubicacion.esDestino === true)
                      .map((ubicacion) => ({
                        label: ubicacion.descripcion,
                        id: ubicacion.id,
                        descripcion: ubicacion.descripcion,
                      }))}
                    value={destinoSelected}
                    onChange={handleDestinoChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar Destino"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <LocationOnIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Buscar y Limpiar filtros */}
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      onClick={aplicarFiltros}
                      startIcon={<SearchIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#5b5b5f",
                        px: 2,
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Buscar
                    </Button>
                    <Tooltip title="Limpiar filtros">
                      <span>
                        <IconButton
                          onClick={limpiarFiltros}
                          disabled={!hayFiltrosActivos}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                          }}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isGeneratingPdf ? "Generando PDF..." : "PDF"}>
                      <span>
                        <IconButton
                          onClick={handleExportVerBalancePdf}
                          disabled={loading || isGeneratingPdf}
                          size="small"
                          sx={{
                            color: alpha("#5b5b5f", 0.9),
                            "&:hover": { backgroundColor: alpha("#5b5b5f", 0.1) },
                          }}
                        >
                          {isGeneratingPdf ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PictureAsPdfIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
        </Paper>

        {/* 🔧 Checkboxes de filtro por estado (Cerrado/Abierto/Cancelado) */}
        <Box sx={{ px: 1, pb: 1 }}>
          <Paper sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                Filtrar por estado:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtroCerrado}
                    onChange={handleFiltroCerradoChange}
                    size="small"
                    sx={{
                      color: theme.palette.success.main,
                      "&.Mui-checked": {
                        color: theme.palette.success.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Cerrado"
                    size="small"
                    color="success"
                    variant={filtroCerrado ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtroAbierto}
                    onChange={handleFiltroAbiertoChange}
                    size="small"
                    sx={{
                      color: theme.palette.info.main,
                      "&.Mui-checked": {
                        color: theme.palette.info.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Abierto"
                    size="small"
                    color="info"
                    variant={filtroAbierto ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
              {<FormControlLabel
                control={
                  <Checkbox
                    checked={filtroCancelado}
                    onChange={handleFiltroCanceladoChange}
                    size="small"
                    sx={{
                      color: theme.palette.error.main,
                      "&.Mui-checked": {
                        color: theme.palette.error.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Cancelado"
                    size="small"
                    color="error"
                    variant={filtroCancelado ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              /> }
            </Box>
          </Paper>
        </Box>
        </Box>
        </Collapse>

        {/* Tabla */}
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            m: 1,
            mt: 0,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            rows={filteredRecetas}
            columns={columns}
            getRowId={(row) => row.id}
            getRowClassName={getRowClassName}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
            density="compact"
            sortingMode="server"
            sortingOrder={["desc", "asc"]}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowHeight={() => 34}
            columnHeaderHeight={40}
            initialState={{
              columns: {
                pinnedColumns: {
                  left: ["fechaInicio", "codigoProductoTerminado", "destino"],
                },
              },
            }}
            columnVisibilityModel={{
              ...columnVisibilityModel,
              ...(!mostrarColumnaPrecioOriginal ? { precioOriginal: false } : {}),
              ...(!mostrarColumnaPrecioTotal ? { precioPorPasajeros: false } : {}),
              ...(!mostrarColumnaCostoTotal ? { costoTotalServicios: false } : {}),
              ...(!mostrarColumnaGanancia ? { ganancia: false } : {}),
              ...(!(mostrarBtnEditarGrupo || mostrarBtnDescargarPdfPasajeros) ? { acciones: false } : {}),
            }}
            onColumnVisibilityModelChange={(newModel) => {
              setColumnVisibilityModel((prev) => {
                const merged = { ...prev, ...newModel }
                if (!mostrarColumnaPrecioOriginal) merged.precioOriginal = false
                if (!mostrarColumnaPrecioTotal) merged.precioPorPasajeros = false
                if (!mostrarColumnaCostoTotal) merged.costoTotalServicios = false
                if (!mostrarColumnaGanancia) merged.ganancia = false
                if (!(mostrarBtnEditarGrupo || mostrarBtnDescargarPdfPasajeros)) merged.acciones = false
                return merged
              })
            }}
            components={{
              Toolbar: GridToolbar,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: false,
              },
            }}
            className="tablaVentasDataGrid"
            sx={{
              width: "100%",
              border: 0,
              height: "100%",
              minWidth: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                //MANEJA CSS HEADER
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "40px !important",
                minHeight: "40px !important",
                maxHeight: "40px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                //MANEJA CSS HEADER
                height: "40px !important",
                minHeight: "40px !important",
                maxHeight: "40px !important",
              },
              // 🔒 Estilos para columnas pinned (sticky nativo de MUI)
              "& .MuiDataGrid-pinnedColumnHeaders": {
                backgroundColor: "#5b5b5f !important",
                boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
              },
              "& .MuiDataGrid-pinnedColumns": {
                boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
              },
              "& .MuiDataGrid-pinnedColumns--left": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#ffffff !important",
              },
              "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#F7F7F7 !important",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#E3EAFD !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                //MANEJA CSS HEADER
                color: "white !important",
                lineHeight: "40px !important",
                fontSize: "0.8rem",
                fontWeight: 700,
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                px: 1,
                py: 0,
                fontSize: "0.82rem",
              },
              "& .MuiDataGrid-cellContent": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-cell[data-field='acciones']": {
                overflow: "visible !important",
                "& .MuiDataGrid-cellContent": {
                  overflow: "visible !important",
                  textOverflow: "clip",
                  whiteSpace: "normal",
                },
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .fila-cambio-pendiente": {
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                },
              },
              "& .fila-cancelada": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.12),
                },
                "& .MuiDataGrid-cell": {
                  opacity: 0.7,
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        {/* Footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"verBalance"}
            onPageChange={(newPage) => {
              const safeTotalPages = Math.max(Number(totalPages) || 1, 1)
              const nextPage = Math.min(Math.max(Number(newPage) || 1, 1), safeTotalPages)

              const idReceta = recetaSelected ? recetaSelected.id : null
              const idDestino = destinoSelected ? destinoSelected.id : null
              let cerradoParam = null
              if (filtroCerrado) cerradoParam = true
              else if (filtroAbierto) cerradoParam = false

              setPage(nextPage)
              fetchData(
                idReceta,
                null,
                idDestino,
                nextPage,
                limit,
                fechaViajeDesde,
                fechaViajeHasta,
                fechaInicioSelected,
                cerradoParam
              )
            }}
            totalPrecioOriginal={totalPrecioOriginal}
            totalPrecioTotal={totalPrecioTotal}
            totalCostoTotal={totalCostoTotal}
            totalGanancia={totalGanancia}
            loadingTotales={loadingTotales}
            mostrarPrecioOriginalVerBalance={mostrarPrecioOriginalFooter}
            mostrarPrecioTotalVerBalance={mostrarPrecioTotalFooter}
            mostrarCostoTotalVerBalance={mostrarCostoTotalFooter}
            mostrarGananciaVerBalance={mostrarGananciaFooter}
          />
        </Box>

        {/* Botón Guardar Estados */}
        {cambiosPendientes.size > 0 && (
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              gap: 2,
              mt: 2,
              mb: 2
            }}
          >
            <Chip
              label={`${cambiosPendientes.size} cambio(s) pendiente(s)`}
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleGuardarEstados}
              sx={{
                bgcolor: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.8),
                },
                fontWeight: 600,
                px: 3
              }}
            >
              Guardar Estados
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearIcon />}
              onClick={() => {
                Swal.fire({
                  title: '¿Descartar cambios?',
                  text: 'Se perderán todos los cambios pendientes sin guardar',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Sí, descartar',
                  cancelButtonText: 'Cancelar'
                }).then((result) => {
                  if (result.isConfirmed) {
                    setCambiosPendientes(new Map())
                    Swal.fire('Cambios descartados', '', 'success')
                  }
                })
              }}
              sx={{ fontWeight: 600 }}
            >
              Descartar Cambios
            </Button>
          </Box>
        )}

        {/* Modal mejorado sutilmente */}
        <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-receta-titulo">
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "85%", lg: "75%" },
              maxWidth: 1000,
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal mejorado */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#5b5b5f",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Detalle de Grupo - Servicios
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedReceta?.descripcionProductoTerminado} • ID: {selectedReceta?.id}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}>
              {selectedReceta && (
                <>
                  {/* Indicador de carga */}
                  {cargandoDatosModal && (
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Cargando datos financieros...
                      </Typography>
                    </Box>
                  )}

                  {/* Información general mejorada */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <BallotIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mb: 1 }} />
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedReceta.totalIngredientes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Servicios
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.success.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                        }}
                      >
                        <MonetizationOnIcon sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
                        <Typography variant="h5" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(precioPaquete)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Precio del Paquete
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.warning.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                        }}
                      >
                        <AttachMoneyIcon sx={{ fontSize: 32, color: theme.palette.warning.main, mb: 1 }} />
                        <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(calcularCostoTotal)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Costo Total
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.info.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                        }}
                      >
                        <AccountBalanceWalletIcon sx={{ fontSize: 32, color: theme.palette.info.main, mb: 1 }} />
                        <Typography variant="h5" color="info.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(calcularGanancia)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ganancia
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                        }}
                      >
                        <PercentIcon sx={{ fontSize: 32, color: theme.palette.secondary.main, mb: 1 }} />
                        <Typography variant="h5" color="secondary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {calcularMargenGanancia.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Margen
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Tabla de ingredientes */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <EditIcon color="primary" />
                      Servicios del Grupo
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cantidad</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Costo Unit.</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Subtotal</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editedIngredientes.map((ingrediente, index) => {
                            const key = `${ingrediente.idMateriaPrima}-${selectedReceta?.destino}`
                            const costoUnitario = preciosCostos[key]?.costo || 0
                            const subtotal = Number.parseFloat(ingrediente.cantidad || 0) * costoUnitario
                            
                            return (
                              <TableRow
                                key={ingrediente.idMateriaPrima}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                  },
                                  "&:nth-of-type(even)": {
                                    backgroundColor: alpha(theme.palette.grey[50], 0.5),
                                  },
                                }}
                              >
                                <TableCell>
                                  <Chip
                                    label={ingrediente.idMateriaPrima}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: "inline-block",
                                    }}
                                  >
                                    {ingrediente.materiaPrima?.codigo || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <BallotIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {ingrediente.materiaPrima?.descripcion || "Sin descripción"}
                                      </Typography>
                                      {generarAtributosSubtexto(ingrediente.materiaPrima) && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block", 
                                            mt: 0.5, 
                                            fontStyle: "italic",
                                            opacity: 0.7,
                                          }}
                                        >
                                          {generarAtributosSubtexto(ingrediente.materiaPrima)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={ingrediente.cantidad === 0 ? "" : ingrediente.cantidad}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      if (value === "") {
                                        handleCantidadChange(index, 0)
                                      } else {
                                        handleCantidadChange(index, value)
                                      }
                                    }}
                                    inputProps={{
                                      step: "0.01",
                                      min: "0",
                                    }}
                                    sx={{
                                      width: 130,
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                                    {formatearMoneda(costoUnitario)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                    {formatearMoneda(subtotal)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Chip
                                    label={ingrediente.eliminado ? "Eliminado" : "Activo"}
                                    size="small"
                                    color={ingrediente.eliminado ? "error" : "success"}
                                    variant={ingrediente.eliminado ? "filled" : "outlined"}
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Resumen financiero */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <TrendingUpIcon color="primary" />
                      Resumen Financiero
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(precioPaquete)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Precio del Paquete
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.warning.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(calcularCostoTotal)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Costo Total
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.info.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(calcularGanancia)} ({calcularMargenGanancia.toFixed(1)}%)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ganancia y Margen
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Alertas de rentabilidad */}
                  {calcularMargenGanancia < 15 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ⚠️ Margen de ganancia bajo ({calcularMargenGanancia.toFixed(1)}%)
                      </Typography>
                      <Typography variant="caption">
                        Considera aumentar el precio de venta o reducir costos para mejorar la rentabilidad.
                      </Typography>
                    </Alert>
                  )}

                  {calcularGanancia < 0 && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ❌ Pérdida por unidad ({formatearMoneda(Math.abs(calcularGanancia))})
                      </Typography>
                      <Typography variant="caption">
                        El precio de venta es menor que el costo. Ajusta los precios urgentemente.
                      </Typography>
                    </Alert>
                  )}

                  {/* Botones de acción */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCloseModal}
                      startIcon={<CloseIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveChanges}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#5b5b5f",
                        px: 3,
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Guardar Cambios
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Modal>

        {/* Modal para mostrar servicios del paquete */}
        <Modal
          open={modalServicios.open}
          onClose={handleCloseModalServicios}
          aria-labelledby="modal-servicios-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "70%", lg: "60%" },
              maxWidth: 800,
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.warning.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BallotIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Servicios del Paquete
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {modalServicios.receta?.descripcionProductoTerminado}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalServicios}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
              {modalServicios.servicios.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <BallotIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay servicios asociados a este paquete
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cantidad</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalServicios.servicios.map((servicio, index) => (
                        <TableRow
                          key={servicio.idMateriaPrima}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.warning.main, 0.02),
                            },
                            "&:nth-of-type(even)": {
                              backgroundColor: alpha(theme.palette.grey[50], 0.5),
                            },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <BallotIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {servicio.materiaPrima?.descripcion || "Sin descripción"}
                                </Typography>
                                {generarAtributosSubtexto(servicio.materiaPrima) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      fontStyle: "italic",
                                      opacity: 0.7,
                                    }}
                                  >
                                    {generarAtributosSubtexto(servicio.materiaPrima)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 500,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                display: "inline-block",
                              }}
                            >
                              {servicio.materiaPrima?.codigo || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {servicio.cantidad || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Botón de cerrar */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModalServicios}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.warning.main,
                    px: 3,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.warning.main, 0.9),
                    },
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        
           {/* Modal para mostrar pasajeros confirmados (lazy) */}
           <Modal
          open={modalPasajerosConfirmados.open}
          onClose={handleCloseModalPasajerosConfirmados}
          aria-labelledby="modal-pasajeros-confirmados-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "60%", lg: "50%" },
              maxWidth: 720,
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.info.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Pasajeros Confirmados
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {modalPasajerosConfirmados.receta?.descripcionProductoTerminado || "Sin grupo"}{" "}
                    {modalPasajerosConfirmados.receta?.destino
                      ? `· ${modalPasajerosConfirmados.receta.destino}`
                      : ""}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalPasajerosConfirmados}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
              {modalPasajerosConfirmados.loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    Cargando pasajeros...
                  </Typography>
                </Box>
              ) : modalPasajerosConfirmados.error ? (
                <Alert severity="error">{modalPasajerosConfirmados.error}</Alert>
              ) : modalPasajerosConfirmados.pasajeros.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <GroupsIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay pasajeros confirmados
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Observaciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalPasajerosConfirmados.pasajeros.map((pasajero, index) => {
                        const nombreCompleto = `${pasajero.descripcion || ""} ${pasajero.apellido || ""}`.trim()

                        return (
                          <TableRow
                            key={`${pasajero.idEntidad || "pasajero"}-${index}`}
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.info.main, 0.02),
                              },
                              "&:nth-of-type(even)": {
                                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {nombreCompleto || "Sin nombre"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>
                              <Typography variant="body2" color="text.secondary">
                                {pasajero.observaciones || "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Botón de cerrar */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModalPasajerosConfirmados}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.info.main,
                    px: 3,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.info.main, 0.9),
                    },
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Modal para editar paquete - GRANDE */}
        <Modal
          open={modalEdicion.open}
          onClose={() => handleCloseModalEdicion()}
          aria-labelledby="modal-edicion-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95vw",
              height: "auto",
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "50px", flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <BallotIcon sx={{ color: theme.palette.primary.main, fontSize: 20, flexShrink: 0 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "0.875rem", flexShrink: 0 }}>
                      Editar Balance
                    </Typography>
                </Box>
                {modalEdicion.paquete && (() => {
                  // Buscar el item completo en itemTresLetras para obtener los atributos
                  const itemCompleto = itemTresLetras?.find(it => it.id === modalEdicion.paquete.idProductoTerminado)
                  
                  // Formatear atributos
                  let attributeValues = ""
                  if (itemCompleto && itemAtributoNoEliminados) {
                    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
                    attributeValues = activeAttributes
                      .map((attr, index) => {
                        const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
                        return attributeValue ? attributeValue : ""
                      })
                      .filter((val) => val.trim() !== "")
                      .join(" • ")
                  }
                  
                  // Construir el texto completo
                  const nombreGrupo = modalEdicion.paquete.descripcionProductoTerminado || "Sin descripción"
                  const atributosTexto = attributeValues ? ` - ${attributeValues}` : ""
                  const destinoTexto = modalEdicion.paquete.destino ? ` con destino a ${modalEdicion.paquete.destino}` : ""
                  
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Typography component="span" variant="body1" sx={{ fontWeight: 600, fontSize: "0.875rem", flexShrink: 0 }}>
                      Grupo:
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        ml: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {nombreGrupo}{atributosTexto}{destinoTexto}
                    </Typography>
                    {modalEdicion.paquete.cancelado && (
                      <Chip
                        label="CANCELADO"
                        size="small"
                        color="error"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "20px",
                          flexShrink: 0
                        }}
                      />
                    )}
                    {modalEdicion.paquete.cerrado && !modalEdicion.paquete.cancelado && (
                      <Chip
                        label="CERRADO"
                        size="small"
                        color="success"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "20px",
                          flexShrink: 0
                        }}
                      />
                    )}
                    </Box>
                    
                  )
                })()}
                </Box>
              <IconButton onClick={() => handleCloseModalEdicion()} size="small" sx={{ flexShrink: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {modalEdicion.paquete && (
                <FormularioVerBalance
                  modoEdicion={true}
                  paqueteInicial={modalEdicion.paquete}
                  mostrarAnaliticos={true}
                  enModal={true}
                  onGuardadoExitoso={handleRecetaGuardada}
                  onCancelar={handleCloseModalEdicion}
                  ocultarCantidadYCostoXCantidadInicial={modalEdicion.ocultarCantidadYCostoXCantidad}
                />
              )}
            </Box>
          </Box>
        </Modal>
        {/* Modal para crear paquete - GRANDE */}
        <Modal
          open={openCrearPaqueteModal}
          onClose={handleCloseCrearPaqueteModal}
          aria-labelledby="modal-crear-paquete-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90vw",
              height: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                <BallotIcon sx={{ color: theme.palette.primary.main, fontSize: 28, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem", flexShrink: 0 }}>
                    Crear Paquete
                  </Typography>
                  {grupoSeleccionadoCrear && (() => {
                    // Buscar el item completo en itemTresLetras para obtener los atributos
                    const itemCompleto = itemTresLetras?.find(it => it.id === grupoSeleccionadoCrear.idItem)
                    
                    // Formatear atributos
                    let attributeValues = ""
                    if (itemCompleto && itemAtributoNoEliminados) {
                      const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
                      attributeValues = activeAttributes
                        .map((attr, index) => {
                          const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
                          return attributeValue ? attributeValue : ""
                        })
                        .filter((val) => val.trim() !== "")
                        .join(" • ")
                    }
                    
                    // Construir el texto completo
                    const nombreGrupo = grupoSeleccionadoCrear.descripcion || "Sin descripción"
                    const atributosTexto = attributeValues ? ` - ${attributeValues}` : ""
                    
                    return (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "rgb(255, 193, 7)", // ✅ Color amarillo RGB
                          fontWeight: 500,
                          mt: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {nombreGrupo}{atributosTexto}
                      </Typography>
                    )
                  })()}
                  {!grupoSeleccionadoCrear && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Define el grupo base y los servicios del paquete
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton onClick={handleCloseCrearPaqueteModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              <FormularioVerBalance
                modoEdicion={false}
                mostrarAnaliticos={true}
                enModal={true}
                onGuardadoExitoso={handlePaqueteCreado}
                onCancelar={handleCloseCrearPaqueteModal}
                ocultarCantidadYCostoXCantidadInicial={paquetesOcultarCantidadFlag}
                onBaseItemChange={setGrupoSeleccionadoCrear} // ✅ Callback para actualizar el grupo seleccionado
              />
            </Box>
          </Box>
        </Modal>
      </Box>
    </ThemeProvider>
  )
}

export default TablaVerBalance




